import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Container, Stack, Typography, Link, Divider } from "@mui/material";

export default function TermsAndConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Términos y condiciones | NovaXFit</title>
        <meta
          name="description"
          content="Consulta los términos y condiciones de NovaXFit sobre registro de usuarios, membresía digital, código QR, beneficios, tienda web y uso de la plataforma."
        />
        <link rel="canonical" href="https://novaxfit.com/terminos-y-condiciones" />
      </Helmet>

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          bgcolor: "#ffffff",
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3.2}>
            <Stack spacing={1.4}>
              <Typography
                sx={{
                  fontSize: { xs: 28, md: 36 },
                  fontWeight: 800,
                  color: "#111111",
                  lineHeight: 1.12,
                }}
              >
                Términos y condiciones
              </Typography>

              <Typography
                sx={{
                  fontSize: 15.5,
                  fontWeight: 500,
                  color: "rgba(17,17,17,0.72)",
                  lineHeight: 1.8,
                }}
              >
                Estos términos regulan el acceso y uso del sitio web, la plataforma de usuario, la visualización de
                membresías, el uso del código QR y los beneficios asociados a NovaXFit. Al registrarse o utilizar la
                plataforma, el usuario acepta estas condiciones.
              </Typography>
            </Stack>

            <Divider />

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                1. Registro y uso de cuenta
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El usuario puede registrarse de forma independiente en la plataforma y será responsable del uso de su cuenta,
                de la confidencialidad de sus credenciales y de toda actividad realizada desde ella.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit podrá suspender o limitar accesos cuando detecte información inconsistente, uso indebido, intento
                de fraude, suplantación de identidad o incumplimiento de estos términos.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                2. Responsabilidad sobre credenciales
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El usuario debe proteger su correo, contraseña y demás credenciales de acceso. NovaXFit no solicitará
                contraseñas por mensajes, llamadas, formularios externos ni canales informales. En caso de sospecha de
                acceso no autorizado, el usuario deberá actualizar sus credenciales inmediatamente.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                3. Código QR y membresía digital
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El código QR asociado a la membresía es personal e intransferible. Su finalidad es permitir la validación
                de membresías activas y el acceso a beneficios o verificaciones autorizadas por NovaXFit.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Está prohibido compartir, revender, prestar o permitir el uso del código QR por parte de otra persona.
                Cualquier intento de uso por un tercero podrá ocasionar restricción, revisión o cancelación del beneficio
                correspondiente y otras medidas administrativas internas.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                4. Fotografía y documento de identificación
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Para acceder a beneficios con partners o validar membresías dentro de las instalaciones, el usuario deberá
                registrar una fotografía propia y un documento válido de identificación, como cédula o pasaporte.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit, su personal o aliados autorizados podrán solicitar la presentación del documento para confirmar que
                la persona que presenta la membresía es el verdadero titular.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                La actualización del documento de identidad deberá hacerse directamente con personal del gimnasio, con el
                fin de proteger la autenticidad del proceso y reducir riesgos de falsificación.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                5. Veracidad de la información
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El usuario declara que la información proporcionada en su cuenta corresponde a datos reales, actualizados y
                verificables. NovaXFit no será responsable por inconvenientes derivados de información falsa, incompleta o
                desactualizada ingresada por el propio usuario.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                6. Beneficios con aliados estratégicos
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Los beneficios, descuentos o promociones ofrecidos por aliados estratégicos están sujetos a disponibilidad,
                validación de membresía activa y cumplimiento de las condiciones específicas definidas por cada partner.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit puede facilitar la validación de la membresía, pero no garantiza condiciones comerciales ajenas al
                control directo del gimnasio. Los términos de los partners pueden consultarse en:
              </Typography>

              <Link
                href="https://novaxfit.com/nuestros-aliados"
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  fontSize: 15.3,
                  fontWeight: 700,
                  color: "#c4121f",
                  wordBreak: "break-word",
                }}
              >
                https://novaxfit.com/nuestros-aliados
              </Link>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                7. Compras en la tienda
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Las compras publicadas en la tienda web se gestionan por medio de WhatsApp con el número de administración
                del gimnasio. La compra no se considera cerrada hasta que se confirme disponibilidad, forma de entrega y
                método de pago con el personal autorizado.
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit podrá corregir errores manifiestos de publicación, disponibilidad o carga involuntaria de datos
                comerciales antes de confirmar definitivamente una entrega.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                8. Conducta permitida dentro de la plataforma
              </Typography>

              <Box component="ul" sx={{ pl: 2.8, m: 0 }}>
                {[
                  "No utilizar identidades falsas ni documentos alterados",
                  "No intentar acceder a cuentas ajenas",
                  "No manipular el sistema de membresías o validaciones",
                  "No usar el QR de otra persona",
                  "No emplear la plataforma con fines fraudulentos, abusivos o contrarios a la ley",
                ].map((item) => (
                  <li key={item}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                9. Disponibilidad del servicio
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit procurará mantener la plataforma disponible, pero no garantiza funcionamiento ininterrumpido en
                todo momento. Pueden existir mantenimientos, actualizaciones, interrupciones temporales o incidencias
                técnicas fuera del control razonable del gimnasio.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                10. Propiedad y uso del contenido
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                La identidad visual, estructura, marca, textos, elementos gráficos y funcionamiento del sitio pertenecen a
                NovaXFit o se usan legítimamente dentro de su operación. No está permitido reproducir, copiar o utilizar
                estos elementos con fines comerciales sin autorización previa.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                11. Protección de datos personales
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                El tratamiento de datos personales se rige por la política de privacidad vigente y por la normativa aplicable.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                12. Modificaciones de los términos
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                NovaXFit podrá actualizar estos términos cuando sea necesario por motivos operativos, legales, comerciales
                o tecnológicos. La versión publicada en el sitio será la vigente desde su publicación.
              </Typography>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#111111" }}>
                13. Contacto
              </Typography>

              <Typography sx={{ fontSize: 15.5, fontWeight: 500, lineHeight: 1.8, color: "#222222" }}>
                Para consultas relacionadas con membresías, validación de identidad, actualización de credenciales o uso de
                la plataforma, el usuario puede comunicarse al <strong>0985224301</strong>.
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
}